/**
 * Convert rrweb events to VizPI telemetry format
 */
function convertRrwebEvents(rrwebEvents) {
  const events = [];
  
  for (const event of rrwebEvents) {
    const converted = convertSingleRrwebEvent(event);
    if (converted) {
      if (Array.isArray(converted)) {
        events.push(...converted);
      } else {
        events.push(converted);
      }
    }
  }
  
  return events;
}

function convertSingleRrwebEvent(event) {
  const { type, data, timestamp } = event;
  
  // Type 3 = IncrementalSnapshot
  if (type === 3 && data) {
    const source = data.source;
    
    // Source 0 = DOM Mutation (for chat messages, terminal output)
    if (source === 0) {
      return convertDomMutation(data, timestamp);
    }
    
    // Source 1 = MouseMove
    if (source === 1 && data.positions?.length > 0) {
      return data.positions.map(pos => {
        // Normalize coordinates to 0-1 range (assuming 1920x1080 viewport)
        const normX = Math.max(0, Math.min(1, pos.x / 1920));
        const normY = Math.max(0, Math.min(1, pos.y / 1080));
        
        return {
          type: 'MOUSE_MOVE',
          event_type: 'MOUSE_MOVE',
          payload: {
            x: Math.round(normX * 10000) / 10000,
            y: Math.round(normY * 10000) / 10000,
            region: getRegionFromPosition(normX, normY)
          },
          timestamp: timestamp + (pos.timeOffset || 0),
          sessionId: null
        };
      });
    }
    
    // Source 2 = MouseInteraction (clicks, etc.)
    if (source === 2) {
      const mouseType = data.type;
      let eventType = null;
      
      // Only capture high-level click events, not individual down/up
      if (mouseType === 2) eventType = 'MOUSE_CLICK';
      else if (mouseType === 4) eventType = 'MOUSE_DBLCLICK';
      
      if (eventType) {
        const normX = Math.max(0, Math.min(1, data.x / 1920));
        const normY = Math.max(0, Math.min(1, data.y / 1080));
        
        return {
          type: eventType,
          event_type: eventType,
          payload: {
            x: Math.round(normX * 10000) / 10000,
            y: Math.round(normY * 10000) / 10000,
            region: getRegionFromPosition(normX, normY)
          },
          timestamp,
          sessionId: null
        };
      }
    }
    
    // Source 5 = Input (for chat typing)
    if (source === 5 && data.text !== undefined) {
      // Filter out language selector values that appear as typing
      const ignoredTexts = ['Python', 'python', 'JavaScript', 'javascript', 'Java', 'java', 'SQL', 'sql', 'C++', 'cpp'];
      if (ignoredTexts.includes(data.text.trim())) {
        return null;
      }
      
      return {
        type: 'CHAT_TYPE',
        event_type: 'CHAT_TYPE',
        payload: {
          text: data.text,
          timestamp
        },
        timestamp,
        sessionId: null
      };
    }
  }
  
  // Type 5 = Custom event
  if (type === 5 && data?.tag) {
    const tag = data.tag;
    const payload = data.payload || {};
    
    if (tag === 'chat_send') {
      return {
        type: 'CHAT_SEND',
        event_type: 'CHAT_SEND',
        payload: {
          text: payload.message || payload.text,
          timestamp
        },
        timestamp,
        sessionId: null
      };
    }
    
    if (tag === 'chat_response') {
      return {
        type: 'CHAT_RESPONSE',
        event_type: 'CHAT_RESPONSE',
        payload: {
          text: payload.message || payload.text,
          timestamp
        },
        timestamp,
        sessionId: null
      };
    }
    
    if (tag === 'terminal_output') {
      return {
        type: 'TERMINAL_OUTPUT',
        event_type: 'TERMINAL_OUTPUT',
        payload: {
          output: payload.output,
          timestamp
        },
        timestamp,
        sessionId: null
      };
    }
  }
  
  return null;
}

function getRegionFromPosition(x, y) {
  if (x < 0.25) {
    return y < 0.5 ? "TASK_DESCRIPTION" : "TEST_CASES";
  }
  if (x > 0.75) {
    return "CHAT_WINDOW";
  }
  return y > 0.7 ? "TERMINAL" : "CODE_EDITOR";
}

function convertDomMutation(data, timestamp) {
  const events = [];
  const adds = data.adds || [];
  const texts = data.texts || [];
  
  const nodeMap = {};
  const parentMap = {};
  
  for (const add of adds) {
    const node = add.node || {};
    if (node.id) {
      nodeMap[node.id] = node;
      parentMap[node.id] = add.parentId;
    }
  }
  
  let hasMessageDiv = false;
  let isAiMessage = false;
  let messageText = '';
  let preTagIds = new Set();
  
  for (const add of adds) {
    const node = add.node || {};
    const attrs = node.attributes || {};
    const className = attrs.class || '';
    const style = attrs.style || '';
    
    if (className === 'message') {
      hasMessageDiv = true;
      isAiMessage = style.includes('justify-content: left') || style.includes('justify-content:left');
    }
    
    if (className === 'messageContentRight') {
      isAiMessage = false;
    }
    if (className === 'messageContent' && !className.includes('Wrapper') && !className.includes('Right')) {
      isAiMessage = true;
    }
    
    if (node.tagName === 'pre') {
      preTagIds.add(node.id);
    }
  }
  
  for (const add of adds) {
    const node = add.node || {};
    const parentId = add.parentId;
    
    if (node.type === 3 && node.textContent && preTagIds.has(parentId)) {
      const text = node.textContent.trim();
      
      const isTimestamp = /^\d{1,2}:\d{2}\s*(AM|PM)?$/i.test(text);
      const isUsername = text.startsWith('Guest_') || text === 'AI assistant';
      const isUiLabel = ['Selection deleted', 'Thinking...'].includes(text);
      
      if (!isTimestamp && !isUsername && !isUiLabel && text.length > 0) {
        if (text.length > messageText.length) {
          messageText = text;
        }
      }
    }
  }
  
  if (hasMessageDiv && messageText) {
    events.push({
      type: isAiMessage ? 'CHAT_RESPONSE' : 'CHAT_SEND',
      event_type: isAiMessage ? 'CHAT_RESPONSE' : 'CHAT_SEND',
      payload: {
        text: messageText,
        content: messageText,
        timestamp
      },
      timestamp,
      sessionId: null
    });
  }
  
  for (const textMutation of texts) {
    const value = textMutation.value || '';
    
    if (value.includes('passed') || value.includes('failed') || 
        value.includes('Error') || value.includes('Traceback') ||
        value.includes('>>>') || value.includes('result')) {
      events.push({
        type: 'TERMINAL_OUTPUT',
        event_type: 'TERMINAL_OUTPUT',
        payload: {
          output: value,
          timestamp
        },
        timestamp,
        sessionId: null
      });
    }
  }
  
  return events.length > 0 ? events : null;
}

export function convertToReplayFormat(rrwebEvents = [], options = {}) {
  const events = convertRrwebEvents(rrwebEvents);
  
  if (events.length === 0) {
    return { events: [], metadata: { duration: 0, eventCounts: {} } };
  }
  
  const timestamps = events.map(e => e.timestamp);
  const sessionStartTime = Math.min(...timestamps);
  const sessionEndTime = Math.max(...timestamps);
  
  const eventCounts = {};
  for (const event of events) {
    const type = event.type || 'UNKNOWN';
    eventCounts[type] = (eventCounts[type] || 0) + 1;
  }
  
  return {
    events,
    metadata: {
      sessionStartTime,
      sessionEndTime,
      duration: sessionEndTime - sessionStartTime,
      eventCounts,
      totalEvents: events.length
    }
  };
}

export default {
  convertToReplayFormat
};